import { useEffect, useRef, useState, useId } from 'react';

const useDarkMode = () => {
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDark(mediaQuery.matches);

        const handler = e => setIsDark(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    return isDark;
};

const GlassSurfaceNavbar = ({
    children,
    height = 60,
    borderRadius = 12,
    borderWidth = 0.05,
    brightness = 60,
    opacity = 0.95,
    blur = 8,
    displace = 0,
    backgroundOpacity = 0.1,
    saturation = 1.2,
    distortionScale = -150,
    redOffset = 0,
    greenOffset = 8,
    blueOffset = 16,
    xChannel = 'R',
    yChannel = 'G',
    mixBlendMode = 'difference',
    className = '',
    style = {}
}) => {
    const uniqueId = useId().replace(/:/g, '-');
    const filterId = `glass-navbar-filter-${uniqueId}`;
    const redGradId = `red-navbar-grad-${uniqueId}`;
    const blueGradId = `blue-navbar-grad-${uniqueId}`;

    const containerRef = useRef(null);
    const feImageRef = useRef(null);
    const redChannelRef = useRef(null);
    const greenChannelRef = useRef(null);
    const blueChannelRef = useRef(null);
    const gaussianBlurRef = useRef(null);

    const isDarkMode = useDarkMode();

    const generateDisplacementMap = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        const actualWidth = rect?.width || window.innerWidth;
        const actualHeight = rect?.height || height;
        const edgeSize = Math.min(actualWidth, actualHeight) * (borderWidth * 0.5);

        const svgContent = `
      <svg viewBox="0 0 ${actualWidth} ${actualHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="${redGradId}" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="red"/>
          </linearGradient>
          <linearGradient id="${blueGradId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="blue"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" fill="black"></rect>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#${redGradId})" />
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#${blueGradId})" style="mix-blend-mode: ${mixBlendMode}" />
        <rect x="${edgeSize}" y="${edgeSize}" width="${actualWidth - edgeSize * 2}" height="${actualHeight - edgeSize * 2}" rx="${borderRadius}" fill="hsl(0 0% ${brightness}% / ${opacity})" style="filter:blur(${blur}px)" />
      </svg>
    `;

        return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
    };

    const updateDisplacementMap = () => {
        feImageRef.current?.setAttribute('href', generateDisplacementMap());
    };

    useEffect(() => {
        updateDisplacementMap();
        [
            { ref: redChannelRef, offset: redOffset },
            { ref: greenChannelRef, offset: greenOffset },
            { ref: blueChannelRef, offset: blueOffset }
        ].forEach(({ ref, offset }) => {
            if (ref.current) {
                ref.current.setAttribute('scale', (distortionScale + offset).toString());
                ref.current.setAttribute('xChannelSelector', xChannel);
                ref.current.setAttribute('yChannelSelector', yChannel);
            }
        });

        gaussianBlurRef.current?.setAttribute('stdDeviation', displace.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        height,
        borderRadius,
        borderWidth,
        brightness,
        opacity,
        blur,
        displace,
        distortionScale,
        redOffset,
        greenOffset,
        blueOffset,
        xChannel,
        yChannel,
        mixBlendMode
    ]);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            setTimeout(updateDisplacementMap, 0);
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTimeout(updateDisplacementMap, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [height]);

    const supportsSVGFilters = () => {
        const isWebkit = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
        const isFirefox = /Firefox/.test(navigator.userAgent);

        if (isWebkit || isFirefox) {
            return false;
        }

        const div = document.createElement('div');
        div.style.backdropFilter = `url(#${filterId})`;
        return div.style.backdropFilter !== '';
    };

    const supportsBackdropFilter = () => {
        if (typeof window === 'undefined') return false;
        return CSS.supports('backdrop-filter', 'blur(10px)');
    };

    const getContainerStyles = () => {
        const baseStyles = {
            ...style,
            width: '100%',
            height: typeof height === 'number' ? `${height}px` : height,
            borderRadius: `${borderRadius}px`,
            '--glass-frost': backgroundOpacity,
            '--glass-saturation': saturation
        };

        const svgSupported = supportsSVGFilters();
        const backdropFilterSupported = supportsBackdropFilter();

        if (svgSupported) {
            return {
                ...baseStyles,
                background: isDarkMode ? `hsl(0 0% 0% / ${backgroundOpacity})` : `hsl(0 0% 100% / ${backgroundOpacity})`,
                backdropFilter: `url(#${filterId}) saturate(${saturation})`,
                boxShadow: isDarkMode
                    ? `0 0 1px 0.5px color-mix(in oklch, white, transparent 70%) inset,
             0 0 8px 3px color-mix(in oklch, white, transparent 90%) inset,
             0px 2px 8px rgba(17, 17, 26, 0.05),
             0px 4px 16px rgba(17, 17, 26, 0.05)`
                    : `0 0 1px 0.5px color-mix(in oklch, black, transparent 90%) inset,
             0 0 8px 3px color-mix(in oklch, black, transparent 95%) inset,
             0px 2px 8px rgba(17, 17, 26, 0.05),
             0px 4px 16px rgba(17, 17, 26, 0.05)`
            };
        } else {
            if (isDarkMode) {
                if (!backdropFilterSupported) {
                    return {
                        ...baseStyles,
                        background: 'rgba(0, 0, 0, 0.5)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`
                    };
                } else {
                    return {
                        ...baseStyles,
                        background: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(10px) saturate(1.5) brightness(1.1)',
                        WebkitBackdropFilter: 'blur(10px) saturate(1.5) brightness(1.1)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`
                    };
                }
            } else {
                if (!backdropFilterSupported) {
                    return {
                        ...baseStyles,
                        background: 'rgba(255, 255, 255, 0.5)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.05)`
                    };
                } else {
                    return {
                        ...baseStyles,
                        background: 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px) saturate(1.5) brightness(1.05)',
                        WebkitBackdropFilter: 'blur(10px) saturate(1.5) brightness(1.05)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.05),
                        inset 0 1px 0 0 rgba(255, 255, 255, 0.3)`
                    };
                }
            }
        }
    };

    const navbarClasses =
        'relative flex items-center transition-opacity duration-[260ms] ease-out';

    return (
        <nav
            ref={containerRef}
            className={`${navbarClasses} ${className}`}
            style={getContainerStyles()}
        >
            <svg
                className="w-full h-full pointer-events-none absolute inset-0 opacity-0 -z-10"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <filter id={filterId} colorInterpolationFilters="sRGB" x="0%" y="0%" width="100%" height="100%">
                        <feImage ref={feImageRef} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="map" />

                        <feDisplacementMap ref={redChannelRef} in="SourceGraphic" in2="map" id="redchannel" result="dispRed" />
                        <feColorMatrix
                            in="dispRed"
                            type="matrix"
                            values="1 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
                            result="red"
                        />

                        <feDisplacementMap
                            ref={greenChannelRef}
                            in="SourceGraphic"
                            in2="map"
                            id="greenchannel"
                            result="dispGreen"
                        />
                        <feColorMatrix
                            in="dispGreen"
                            type="matrix"
                            values="0 0 0 0 0
                      0 1 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
                            result="green"
                        />

                        <feDisplacementMap ref={blueChannelRef} in="SourceGraphic" in2="map" id="bluechannel" result="dispBlue" />
                        <feColorMatrix
                            in="dispBlue"
                            type="matrix"
                            values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 1 0 0
                      0 0 0 1 0"
                            result="blue"
                        />

                        <feBlend in="red" in2="green" mode="screen" result="rg" />
                        <feBlend in="rg" in2="blue" mode="screen" result="output" />
                        <feGaussianBlur ref={gaussianBlurRef} in="output" stdDeviation="0.5" />
                    </filter>
                </defs>
            </svg>

            <div className="w-full h-full flex items-center px-4 md:px-6 lg:px-8 rounded-[inherit] relative z-10">
                {children}
            </div>
        </nav>
    );
};

export default GlassSurfaceNavbar;
